/**
 * Formatter utilities for consistent data display
 * Provides formatting functions for currency, numbers, percentages, dates, and time
 */

/**
 * Format a number as currency (USD)
 */
export function formatCurrency(
  value: number | null | undefined,
  currency?: string,
  options?: { compact?: boolean }
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0.00';
  }
  const currencyCode = currency || 'USD';
  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  if (options?.compact) {
    formatOptions.notation = 'compact';
    formatOptions.compactDisplay = 'short';
  }
  return new Intl.NumberFormat('en-US', formatOptions).format(value);
}

/**
 * Format a number with optional decimal places
 */
export function formatNumber(
  value: number | null | undefined,
  options?: { decimals?: number }
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  const decimals = options?.decimals ?? 2;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a number as a percentage
 */
export function formatPercentage(
  value: number | null | undefined,
  options?: { decimals?: number }
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  const decimals = options?.decimals ?? 2;
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
}

/**
 * Format a date
 */
export function formatDate(
  value: string | Date | null | undefined,
  options?: { dateStyle?: 'full' | 'long' | 'medium' | 'short'; timeStyle?: 'full' | 'long' | 'medium' | 'short' }
): string {
  if (!value) {
    return 'N/A';
  }
  const date = new Date(value);
  if (options?.timeStyle) {
    // If timeStyle is provided, format as date and time
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: options.dateStyle === 'short' ? 'numeric' : 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(options.timeStyle === 'full' && { second: '2-digit' }),
    });
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: options?.dateStyle === 'short' ? 'numeric' : 'short',
    day: 'numeric',
  });
}

/**
 * Format a date and time
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) {
    return 'N/A';
  }
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a timestamp as "time ago" (e.g., "2 minutes ago", "1 hour ago")
 */
export function formatTimeAgo(value: string | Date | number | null | undefined): string {
  if (!value) {
    return 'N/A';
  }

  const now = new Date();
  const past = new Date(value);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  if (diffInSeconds < 0) {
    return 'just now';
  }

  if (diffInSeconds < 60) {
    return diffInSeconds === 1 ? '1 second ago' : `${diffInSeconds} seconds ago`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`;
}

